import { Node } from "@xyflow/react";
import { DefaultNodeData, ZapNodeData, NodeData } from "@zapier/canvas-state";
import { ASSET_APP_IDS } from "@zapier/canvas-constants";

type KeysToClearFromDefaultNode = keyof Pick<
  DefaultNodeData,
  | "label"
  | "app"
  | "services"
  | "tableId"
  | "projectId"
  | "pageId"
  | "chatbotId"
  | "agentId"
  | "color"
  | "link"
>;
type KeysToClearFromZapNode = keyof ZapNodeData;
type KeysToClearWhenNoAppSelected = keyof Pick<
  DefaultNodeData,
  "tableId" | "projectId" | "pageId" | "chatbotId" | "agentId" | "app" | "link"
>;

type DataKeysByNodeType = {
  default: KeysToClearFromDefaultNode[];
  zap: KeysToClearFromZapNode[];
};

const dataKeysByNodeType: DataKeysByNodeType = {
  default: [
    "app",
    "services",
    "tableId",
    "projectId",
    "pageId",
    "chatbotId",
    "agentId",
    "link",
  ],
  zap: ["zapId", "isExpanded"],
};

type ClearableKey =
  | "tableId"
  | "projectId"
  | "pageId"
  | "chatbotId"
  | "agentId"
  | "link";

const keysToKeepByAssetType = {
  tables: ["tableId"] as ClearableKey[],
  interfaces: ["projectId", "pageId"] as ClearableKey[],
  chatbots: ["chatbotId"] as ClearableKey[],
  agents: ["agentId"] as ClearableKey[],
};

const keysToClearForNonAssetNodes: ClearableKey[] = [
  "tableId",
  "projectId",
  "pageId",
  "chatbotId",
  "agentId",
];

const keysToClearWhenNoApp: KeysToClearWhenNoAppSelected[] = [
  "app",
  "tableId",
  "projectId",
  "pageId",
  "chatbotId",
  "agentId",
];

function clearKeys(data: object, keysToClear: string[]) {
  keysToClear.forEach((key) => {
    delete (data as { [key: string]: unknown })[key];
  });
}

/**
 * Clears out specific data fields from the given node based on its type.
 *
 * For nodes of type 'zap', fields specific to 'default' nodes are removed.
 * For nodes of type 'default', fields specific to 'zap' nodes are removed.
 *
 * Key behaviors:
 * - Preserves color for asset nodes (tables, interfaces, chatbots)
 * - Clears color when converting from non-asset to asset nodes
 * - Clears link for asset nodes, preserves for non-asset nodes
 *
 * App-specific field handling:
 * - For tables: keeps tableId, clears projectId/pageId/chatbotId
 * - For interfaces: keeps projectId/pageId, clears tableId/chatbotId
 * - For chatbots: keeps chatbotId, clears tableId/projectId/pageId
 * - For agents: keeps agentId, clears tableId/projectId/pageId/chatbotId
 */
export function clearOutDataFromNewNode(
  newNode: Node<NodeData>,
): Node<NodeData> {
  if (!newNode.data) {
    return newNode;
  }

  // Create a new data object to avoid mutating the original
  const newData = { ...newNode.data };

  let keysToClear: string[] = [];

  if (newNode.type === "zap") {
    keysToClear = dataKeysByNodeType.default;
  } else if (newNode.type === "default") {
    keysToClear = dataKeysByNodeType.zap;
  }

  clearKeys(newData, keysToClear);

  if (
    !("services" in newData) ||
    !newData.services ||
    newData.services.length === 0
  ) {
    clearKeys(newData, keysToClearWhenNoApp);
  } else {
    const appId: string = newData.services[0].currentImplementation.id;

    const isTableApp = appId.includes(ASSET_APP_IDS.TABLES);
    const isInterfacesApp = appId.includes(ASSET_APP_IDS.INTERFACES);
    const isChatbotsApp = appId.includes(ASSET_APP_IDS.CHATBOTS);
    const isAgentsApp = appId.includes(ASSET_APP_IDS.AGENTS);

    if (isTableApp) {
      // Keep only tableId, clear other asset keys and link
      clearKeys(
        newData,
        [...keysToClearForNonAssetNodes, "link" as ClearableKey].filter(
          (key) => !keysToKeepByAssetType.tables.includes(key),
        ),
      );
    } else if (isInterfacesApp) {
      // Keep only projectId and pageId, clear other asset keys and link
      clearKeys(
        newData,
        [...keysToClearForNonAssetNodes, "link" as ClearableKey].filter(
          (key) => !keysToKeepByAssetType.interfaces.includes(key),
        ),
      );
    } else if (isChatbotsApp) {
      // Keep only chatbotId, clear other asset keys and link
      clearKeys(
        newData,
        [...keysToClearForNonAssetNodes, "link" as ClearableKey].filter(
          (key) => !keysToKeepByAssetType.chatbots.includes(key),
        ),
      );
    } else if (isAgentsApp) {
      // Keep only agentId, clear other asset keys and link
      clearKeys(
        newData,
        [...keysToClearForNonAssetNodes, "link" as ClearableKey].filter(
          (key) => !keysToKeepByAssetType.agents.includes(key),
        ),
      );
    } else {
      // For all other app types, clear all asset keys but keep link
      clearKeys(newData, keysToClearForNonAssetNodes);
    }
  }

  return { ...newNode, data: newData };
}
